import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";
import { verifications } from "./verify";
import { type Answers, type Ending, type FormDefinition, isField } from "@fobu/schema";
import {
  type AnswerIssue,
  computeFlow,
  type FlowState,
  type FormIndex,
  indexForm,
  isEmptyValue,
  validateAnswer,
  validatePage,
  checkSubmission,
} from "@fobu/engine";
import { type FormClient, randomId, type ResumeLink, type ResumeState } from "./client";
import { clearDraft, loadDraft, markSubmitted, saveDraft } from "./draft";

export type Errors = Record<string, AnswerIssue[]>;

/** Ein Bildschirm im Conversational-/Funnel-Modus: Inhaltsblöcke + höchstens ein Feld. */
export interface Step {
  id: string;
  pageId: string;
  blockIds: string[];
  fieldId: string | null;
}

export interface ControllerOptions {
  def: FormDefinition;
  versionId: string;
  client: FormClient;
  /** Schlüssel für den lokalen Entwurf (z. B. Slug + Version). `null` = kein Entwurf. */
  draftKey: string | null;
  initialAnswers?: Record<string, unknown>;
  hidden?: Record<string, string>;
  /** Formular-Schlüssel (z. B. Slug) für „nur einmal pro Gerät“. */
  onceKey?: string;
  /** „Sonstiges“-Texte vorbelegen (Selbstbearbeitung). */
  initialOther?: Record<string, string>;
  /** Zwischenstand aus einem Fortsetzungslink – hat Vorrang vor dem lokalen Entwurf. */
  resume?: ResumeState & { token: string };
  /** Feld, dessen Antwort aus der Einladungsmail stammt. */
  fromMail?: string;
}

export type Status = "filling" | "submitting" | "done";

export function buildSteps(flow: FlowState, idx: FormIndex): Step[] {
  const steps: Step[] = [];
  for (const page of flow.pages) {
    let pending: string[] = [];
    for (const blockId of page.visibleBlockIds) {
      const block = idx.blockById.get(blockId)!.block;
      if (isField(block)) {
        steps.push({ id: block.id, pageId: page.pageId, blockIds: [...pending, block.id], fieldId: block.id });
        pending = [];
      } else pending.push(blockId);
    }
    if (pending.length) steps.push({ id: pending[0]!, pageId: page.pageId, blockIds: pending, fieldId: null });
  }
  return steps;
}

export function useFormController(opts: ControllerOptions) {
  const { def, versionId, client, draftKey } = opts;
  const idx = useMemo(() => indexForm(def), [def]);
  const conversational = def.settings.mode !== "classic";

  const resume = opts.resume;
  /** Lokaler Entwurf (nur dieses Gerät). Stammt er aus einer älteren Fassung, nur noch vorhandene Fragen übernehmen. */
  const draft = useMemo(() => {
    if (resume || !draftKey || !def.settings.savePartial) return null;
    const d = loadDraft(draftKey, `${draftKey}:${versionId}`);
    if (!d || !d.versionId || d.versionId === versionId) return d ? { ...d, changed: false } : null;
    const keep = <T,>(o: Record<string, T>) => Object.fromEntries(Object.entries(o).filter(([k]) => idx.fieldById.has(k)));
    return { ...d, answers: keep(d.answers), other: keep(d.other), changed: true };
  }, [draftKey, def, resume, versionId, idx]);
  const [answers, setAnswers] = useState<Record<string, unknown>>(() => ({ ...(resume?.answers ?? draft?.answers ?? {}), ...(opts.initialAnswers ?? {}) }));
  const [other, setOther] = useState<Record<string, string>>(() => resume?.other ?? draft?.other ?? opts.initialOther ?? {});
  /** Token des Fortsetzungslinks – derselbe Link wird bei erneutem Speichern aktualisiert. */
  const [resumeToken, setResumeToken] = useState<string | undefined>(() => resume?.token ?? draft?.resumeToken);
  const [resumedAt, setResumedAt] = useState<string | null>(() => resume?.savedAt ?? null);
  const [hidden] = useState<Record<string, string>>(() => opts.hidden ?? {});
  const [errors, setErrors] = useState<Errors>({});
  const [summaryVisible, setSummaryVisible] = useState(false);
  /** Zählt fehlgeschlagene Absende-/Weiter-Versuche – nur dann bekommt die Fehlerübersicht den Fokus. */
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState<Status>("filling");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [ending, setEnding] = useState<{ ending: Ending; redirectUrl?: string; flow: FlowState; waitlisted?: boolean; updated?: boolean; paymentToken?: string; resultsToken?: string } | null>(null);
  const [draftRestored, setDraftRestored] = useState(!!draft && Object.keys(draft.answers).length > 0);
  const [seed] = useState(() => Math.random().toString(36).slice(2));
  /** Ein Schlüssel je Absendevorgang (auch über Neuladen per Entwurf) – Wiederholen erzeugt keine Doppelantwort. */
  const [submissionKey, setSubmissionKey] = useState(() => draft?.submissionKey ?? randomId());
  /** Richtung der letzten Navigation – für Übergangsanimationen. 0 = erster Bildschirm, keine Animation. */
  const [direction, setDirection] = useState<1 | -1 | 0>(0);
  /** Prüfansicht vor dem Absenden (Einstellung „Angaben vor dem Absenden prüfen“). */
  const [reviewing, setReviewing] = useState(false);

  const flow = useMemo(() => computeFlow(def, { answers, hidden }), [def, answers, hidden]);
  const flowRef = useRef(flow);
  flowRef.current = flow;
  const steps = useMemo(() => (conversational ? buildSteps(flow, idx) : []), [conversational, flow, idx]);

  const firstPosition = conversational ? (steps[0]?.id ?? null) : (flow.pages[0]?.pageId ?? null);
  /** Antwort aus der Mail: bei der ersten Seite bzw. Frage ohne Antwort beginnen (sonst bei der letzten, zum Absenden). */
  const [mailStart] = useState<string | null>(() => {
    if (!opts.fromMail || resume) return null;
    const open = (ids: string[]) => ids.some((id) => isEmptyValue(flow.answers[id]));
    const pos = conversational ? steps.find((s) => s.fieldId && open([s.fieldId]))?.id ?? steps.at(-1)?.id : flow.pages.find((p) => open(p.visibleFieldIds))?.pageId ?? flow.pages.at(-1)?.pageId;
    return pos ?? null;
  });
  const [position, setPosition] = useState<string | null>(() => resume?.position ?? mailStart ?? draft?.position ?? null);

  // Position gegen den aktuellen Pfad absichern (z. B. nach Änderung einer früheren Antwort).
  const positions = conversational ? steps.map((s) => s.id) : flow.pages.map((p) => p.pageId);
  const current = position && positions.includes(position) ? position : firstPosition;
  const currentIndex = current ? positions.indexOf(current) : 0;
  const isLast = currentIndex === positions.length - 1;

  /* ---------- Entwurf speichern ---------- */
  useEffect(() => {
    if (!draftKey || !def.settings.savePartial || status === "done") return;
    const t = setTimeout(() => saveDraft(draftKey, { answers, other, position: current, submissionKey, versionId, ...(resumeToken ? { resumeToken } : {}) }), 400);
    return () => clearTimeout(t);
  }, [answers, other, current, draftKey, def, status, submissionKey, resumeToken, versionId]);

  const discardDraft = useCallback(() => {
    if (draftKey) clearDraft(draftKey);
    setAnswers({ ...(opts.initialAnswers ?? {}) });
    setOther({});
    setErrors({});
    setPosition(null);
    setDraftRestored(false);
    setSubmissionKey(randomId());
  }, [draftKey, opts.initialAnswers]);

  /* ---------- Antworten ---------- */
  const errorsRef = useRef(errors);
  errorsRef.current = errors;

  const revalidate = useCallback(
    (fieldId: string, value: unknown, otherText?: string) => {
      if (!errorsRef.current[fieldId]) return;
      const field = idx.fieldById.get(fieldId);
      if (!field) return;
      const f = flowRef.current;
      const issues = validateAnswer(field, value, otherText, { answers: { ...f.answers, [fieldId]: value }, hidden: f.hidden, vars: f.variables });
      setErrors((e) => {
        const next = { ...e };
        if (issues.length) next[fieldId] = issues;
        else delete next[fieldId];
        if (Object.keys(next).length === 0) setSummaryVisible(false);
        return next;
      });
    },
    [idx],
  );

  // Ladezeitpunkt (Ausfüllzeit, Spam-Schutz) und Honeypot-Wert.
  const startedAt = useMemo(() => Date.now(), []);
  const honeypot = useRef("");
  const started = useRef(false);

  const setAnswer = useCallback(
    (fieldId: string, value: unknown) => {
      if (!started.current) {
        started.current = true;
        client.markStarted?.(versionId);
      }
      setAnswers((a) => {
        const next = { ...a };
        if (isEmptyValue(value)) delete next[fieldId];
        else next[fieldId] = value;
        return next;
      });
      setSubmitError(null);
      revalidate(fieldId, value, other[fieldId]);
    },
    [revalidate, other, client, versionId],
  );

  const setOtherText = useCallback(
    (fieldId: string, text: string) => {
      setOther((o) => ({ ...o, [fieldId]: text }));
      revalidate(fieldId, answers[fieldId], text);
    },
    [revalidate, answers],
  );

  /* ---------- Laufende Übertragungen (Uploads, Unterschrift) ---------- */
  // Formularweit registriert: Weiter/Absenden warten, bis alles übertragen ist, statt ein Pflichtfeld
  // fälschlich als leer zu melden oder eine Antwort ohne Datei abzuschicken.
  const transfers = useRef(new Set<Promise<unknown>>());
  const [pendingTransfers, setPendingTransfers] = useState(0);
  const [waitingFor, setWaitingFor] = useState<"next" | "submit" | null>(null);
  const track = useCallback(<T,>(p: Promise<T>): Promise<T> => {
    transfers.current.add(p);
    setPendingTransfers(transfers.current.size);
    const done = () => {
      transfers.current.delete(p);
      setPendingTransfers(transfers.current.size);
    };
    p.then(done, done);
    return p;
  }, []);

  // Gewählte, aber nicht hochgeladene Dateien: nie still ohne sie weiter – erst wiederholen oder bewusst verzichten.
  const failedUploads = useRef(new Map<string, string>());
  /** Feld meldet eine fehlgeschlagene Übertragung (Meldungstext) oder dass sie erledigt ist (null). */
  const uploadFailed = useCallback((fieldId: string, message: string | null) => {
    if (message) return void failedUploads.current.set(fieldId, message);
    failedUploads.current.delete(fieldId);
    setErrors((e) => {
      if (e[fieldId]?.[0]?.code !== "upload_failed") return e;
      const n = { ...e };
      delete n[fieldId];
      return n;
    });
  }, []);
  const uploadErrors = (only?: (fieldId: string) => boolean): Errors => {
    const out: Errors = {};
    for (const [id, message] of failedUploads.current) if (!only || only(id)) out[id] = [{ code: "upload_failed", message }];
    return out;
  };

  /* ---------- Navigation & Absenden ---------- */
  const goTo = useCallback((pos: string, dir: 1 | -1) => {
    setDirection(dir);
    setPosition(pos);
    setSummaryVisible(false);
    setReviewing(false);
  }, []);

  /** Fehler zeigen und zur ersten betroffenen Stelle springen. */
  const showErrors = (errs: Errors, pageId?: string | null) => {
    setErrors(errs);
    setSummaryVisible(true);
    setAttempt((a) => a + 1);
    const target = conversational ? steps.find((s) => s.fieldId && errs[s.fieldId])?.id : (pageId ?? idx.blockById.get(Object.keys(errs)[0]!)?.page.id);
    if (target) goTo(target, -1);
  };

  const submit = useCallback(async () => {
    if (transfers.current.size) return setWaitingFor("submit");
    const blocked = uploadErrors();
    if (Object.keys(blocked).length) return showErrors(blocked);
    const result = checkSubmission(def, { answers, other, hidden });
    if (!result.ok) return showErrors(result.errors, result.firstInvalidPageId);
    setStatus("submitting");
    setSubmitError(null);
    const res = await client.submit(versionId, { answers: result.data.answers as Answers, other: result.data.other, hidden: result.data.hidden, sessionId: seed, submissionKey, ...(resumeToken ? { resumeToken } : {}), language: def.settings.language, startedAt, ...withVerifications(result.data.answers), ...(honeypot.current ? { _hp: honeypot.current } : {}) });
    if (!res.ok) {
      setStatus("filling");
      // Zum ersten betroffenen Feld zurück (z. B. Termin inzwischen ausgebucht).
      if (res.errors && Object.keys(res.errors).length) showErrors(res.errors);
      else setSubmitError(res.message ?? "network");
      return;
    }
    if (draftKey) clearDraft(draftKey);
    if (opts.onceKey && def.settings.limitOnePer === "browser") markSubmitted(opts.onceKey);
    const serverEnding = res.endingId ? idx.endingById.get(res.endingId) : undefined;
    const redirect = res.redirectUrl ?? result.data.redirectUrl;
    setEnding({ ending: serverEnding ?? flow.ending, ...(redirect ? { redirectUrl: redirect } : {}), flow, ...(res.waitlisted ? { waitlisted: true } : {}), ...(res.updated ? { updated: true } : {}), ...(res.payment ? { paymentToken: res.payment.token } : {}), ...(res.resultsToken ? { resultsToken: res.resultsToken } : {}) });
    setStatus("done");
  }, [def, answers, other, hidden, client, versionId, draftKey, idx, flow, conversational, steps, goTo, seed, submissionKey, resumeToken]);

  /** Vor dem Absenden: alles prüfen; ist alles gültig, die Zusammenfassung zeigen. */
  const review = useCallback(() => {
    const blocked = uploadErrors();
    if (Object.keys(blocked).length) return showErrors(blocked);
    const result = checkSubmission(def, { answers, other, hidden });
    if (!result.ok) return showErrors(result.errors, result.firstInvalidPageId);
    setDirection(1);
    setReviewing(true);
  }, [def, answers, other, hidden, conversational, steps, goTo]);

  /** Aus der Prüfansicht zurück zu einem Abschnitt (Seite) bzw. dessen erster Frage. */
  const editSection = useCallback(
    (pageId: string) => {
      const target = conversational ? steps.find((s) => s.pageId === pageId)?.id : pageId;
      if (target) goTo(target, -1);
    },
    [conversational, steps, goTo],
  );

  /** „Später weitermachen“: Zwischenstand sichern, Link (optional per E-Mail) erhalten. */
  const saveForLater = useCallback(
    async (email?: string): Promise<ResumeLink> => {
      if (!client.saveResume) throw new Error("not_supported");
      const link = await client.saveResume(versionId, { answers, other, hidden, position: current, ...(resumeToken ? { token: resumeToken } : {}), ...(email ? { email } : {}) });
      setResumeToken(link.token);
      return link;
    },
    [client, versionId, answers, other, hidden, current, resumeToken],
  );

  const next = useCallback(async () => {
    if (!current || status !== "filling") return;
    if (transfers.current.size) return setWaitingFor("next");
    let pageErrors: Errors;
    if (conversational) {
      const step = steps[currentIndex];
      const field = step?.fieldId ? idx.fieldById.get(step.fieldId) : undefined;
      const issues = field ? validateAnswer(field, flow.answers[field.id], other[field.id], { answers: flow.answers, hidden: flow.hidden, vars: flow.variables }) : [];
      pageErrors = field && issues.length ? { [field.id]: issues } : {};
    } else {
      pageErrors = validatePage(def, current, { answers, other, hidden });
    }
    const here = conversational ? steps[currentIndex]?.fieldId : undefined;
    pageErrors = { ...pageErrors, ...uploadErrors((id) => (conversational ? id === here : idx.blockById.get(id)?.page.id === current)) };
    if (Object.keys(pageErrors).length) {
      setErrors((e) => ({ ...e, ...pageErrors }));
      setSummaryVisible(true);
      setAttempt((a) => a + 1);
      return;
    }
    const pageId = conversational ? steps[currentIndex]!.pageId : current;
    if (isLast) return def.settings.reviewBeforeSubmit ? review() : submit();
    const nextPos = positions[currentIndex + 1]!;
    const nextPageId = conversational ? steps[currentIndex + 1]!.pageId : nextPos;
    // Wie `collectsPartials` aus @fobu/schema – hier inline, damit das Formular-Bundle keine Schemas lädt.
    if (client.savePartial && (def.settings.collectPartials ?? def.settings.savePartial) && nextPageId !== pageId) {
      void client.savePartial(versionId, { answers: flow.answers as Answers, other, hidden, pageId, sessionId: seed });
    }
    goTo(nextPos, 1);
  }, [current, status, conversational, steps, currentIndex, idx, flow, other, def, answers, hidden, isLast, submit, review, positions, client, versionId, goTo]);

  /**
   * Auto-Weiter (Einfachauswahl, Ja/Nein, Bewertung im Conversational-Modus): erst nach dem
   * Re-Render mit der neuen Antwort weitergehen, kurz verzögert, damit die Auswahl sichtbar wird.
   */
  const [advanceToken, setAdvanceToken] = useState(0);
  const requestAdvance = useCallback(() => setAdvanceToken((t) => t + 1), []);
  const nextRef = useRef(next);
  nextRef.current = next;
  useEffect(() => {
    if (advanceToken === 0) return;
    const reduce = globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const t = setTimeout(() => void nextRef.current(), reduce ? 0 : 280);
    return () => clearTimeout(t);
  }, [advanceToken]);

  // Sobald alle Übertragungen fertig sind, die gewünschte Aktion mit dem aktuellen Stand ausführen.
  const submitRef = useRef(submit);
  submitRef.current = submit;
  useEffect(() => {
    if (!waitingFor || pendingTransfers > 0) return;
    const action = waitingFor;
    setWaitingFor(null);
    void (action === "submit" ? submitRef.current() : nextRef.current());
  }, [waitingFor, pendingTransfers]);

  const back = useCallback(() => {
    if (currentIndex > 0) goTo(positions[currentIndex - 1]!, -1);
  }, [currentIndex, positions, goTo]);

  return {
    /** Honeypot-Feld: Wert wird mitgeschickt; Menschen sehen es nie. */
    setHoneypot: (v: string) => {
      honeypot.current = v;
    },
    def,
    idx,
    flow,
    steps,
    conversational,
    answers,
    other,
    hidden,
    errors,
    summaryVisible,
    attempt,
    status,
    submitError,
    ending,
    draftRestored,
    /** Hinweis „Antwort aus der Mail“: `here` = die Frage steht auf diesem Bildschirm. */
    fromMail:
      mailStart && current === mailStart && status === "filling" && opts.fromMail
        ? { here: conversational ? steps.find((s) => s.id === current)?.fieldId === opts.fromMail : !!flow.pages.find((p) => p.pageId === current)?.visibleFieldIds.includes(opts.fromMail) }
        : null,
    /** Zeitpunkt des lokalen Entwurfs und ob er aus einer älteren Fassung stammt. */
    draftSavedAt: draft?.savedAt ?? null,
    draftChanged: !!draft?.changed,
    dismissDraftNotice: () => setDraftRestored(false),
    reviewing,
    editSection,
    goToPage: (pageId: string) => goTo(pageId, -1),
    discardDraft,
    seed,
    direction,
    current,
    currentIndex,
    total: positions.length,
    isLast,
    setAnswer,
    setOtherText,
    next,
    requestAdvance,
    back,
    submit,
    saveForLater,
    canSaveForLater: !!client.saveResume && def.settings.resumeLinks,
    /** Zeitpunkt des geladenen Zwischenstands (Fortsetzungslink) für den Hinweis. */
    resumedAt,
    dismissResumeNotice: () => setResumedAt(null),
    /** Übertragung registrieren (Upload, Unterschrift). */
    track,
    uploadFailed,
    /** Es laufen noch Übertragungen. */
    transfersPending: pendingTransfers > 0,
    /** Weiter/Absenden wartet gerade auf laufende Übertragungen. */
    waitingForTransfers: waitingFor !== null,
  };
}

export type Controller = ReturnType<typeof useFormController>;

const withVerifications = (answers: Record<string, unknown>) => {
  const v = verifications(answers);
  return v ? { verifications: v } : {};
};
